import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';

// When a save is held for approval (deleting in a module whose deletes need
// approval), the server says so in an X-Approval header and the API client
// raises "approval-notice". Every screen gets this banner without changing it,
// so "sent for approval" is never mistaken for "deleted" — even when a screen
// shows its own "deleted" message at the same moment.

const SHOW_MS = 10_000;

const ApprovalNotice = () => {
  const { isDarkMode } = useTheme();
  const t = getTokens(isDarkMode);
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  // A hold ("sent for approval") or just information ("2 waiting changes were cancelled").
  const [isInfo, setIsInfo] = useState(false);

  useEffect(() => {
    let timer: number | undefined;
    const show = (e: Event) => {
      const text = (e as CustomEvent<string>).detail;
      if (!text) return;
      setMessage(text);
      setIsInfo(e.type === 'approval-info');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setMessage(null), SHOW_MS);
    };
    window.addEventListener('approval-notice', show);
    window.addEventListener('approval-info', show);
    return () => {
      window.removeEventListener('approval-notice', show);
      window.removeEventListener('approval-info', show);
      window.clearTimeout(timer);
    };
  }, []);

  if (!message) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: '72px', left: '50%', transform: 'translateX(-50%)', zIndex: 3000,
        maxWidth: 'min(640px, calc(100vw - 32px))', padding: '12px 16px', borderRadius: t.radius.lg,
        backgroundColor: t.bgRaised, color: t.textMain, border: `2px solid ${isInfo ? t.border : t.warning}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'flex', gap: '12px', alignItems: 'center'
      }}
    >
      <span style={{ fontSize: '20px' }}>{isInfo ? 'ℹ️' : '🕒'}</span>
      <span style={{ flex: 1, fontSize: t.fontSize.md }}>{message}</span>
      <button
        type="button"
        onClick={() => { setMessage(null); navigate('/approvals'); }}
        style={{
          border: `1px solid ${t.border}`, background: 'transparent', color: t.primaryText,
          borderRadius: t.radius.sm, padding: '4px 10px', cursor: 'pointer', fontWeight: 'bold'
        }}
      >
        Approvals
      </button>
      <button
        type="button" aria-label="Close" onClick={() => setMessage(null)}
        style={{ border: 'none', background: 'transparent', color: t.textMuted, cursor: 'pointer', fontSize: '18px' }}
      >
        ×
      </button>
    </div>
  );
};

export default ApprovalNotice;
